import type { LaunchableWindowKind } from '../data/protocol/types';

export type ShortcutAction =
  | { type: 'news' }
  | { type: 'quote'; symbol: string }
  | { type: 'chart'; symbol: string }
  | { type: 'add' }
  | { type: 'reset' }
  | { type: 'help' };

export type ShortcutParseResult =
  | { ok: true; action: ShortcutAction }
  | { ok: false; message: string };

export type ShortcutHelpItem = {
  command: string;
  description: string;
};

export type ShortcutParseOptions = {
  availableSymbols?: readonly string[];
};

export const SHORTCUT_COMMAND_PLACEHOLDER = '/ Type help to see all available commands';

export const SHORTCUT_HELP: ShortcutHelpItem[] = [
  {
    command: '/NEWS',
    description: 'Open a market news window'
  },
  {
    command: '/QUOTE <ticker>',
    description: 'Open a single-symbol quote window'
  },
  {
    command: '/CHART <ticker>',
    description: 'Open a graph window for a symbol'
  },
  {
    command: '/ADD',
    description: 'Open the Add Window dialog'
  },
  {
    command: '/RESET',
    description: 'Reset the workspace to the default layout'
  },
  {
    command: '/HELP',
    description: 'Open the keyboard shortcuts dialog'
  }
];

const WINDOW_SHORTCUT_HINTS: Partial<Record<LaunchableWindowKind, string>> = {
  quote: 'Type /quote <ticker>',
  graph: 'Type /chart <ticker>',
  news: 'Type /news'
};

export function getWindowShortcutHint(kind: LaunchableWindowKind): string | undefined {
  return WINDOW_SHORTCUT_HINTS[kind];
}

export function parseShortcutCommand(
  input: string,
  options: ShortcutParseOptions = {}
): ShortcutParseResult {
  const value = input.trim();

  if (!value) {
    return { ok: false, message: 'Enter a command.' };
  }

  const normalizedValue = value.startsWith('/') ? value.slice(1).trim() : value;
  const parts = normalizedValue.split(/\s+/);
  const command = parts[0].toUpperCase();
  const args = parts.slice(1);

  if (command === 'NEWS') {
    if (args.length > 0) {
      return { ok: false, message: 'Use /NEWS.' };
    }

    return { ok: true, action: { type: 'news' } };
  }

  if (command === 'ADD') {
    if (args.length > 0) {
      return { ok: false, message: 'Use /ADD.' };
    }

    return { ok: true, action: { type: 'add' } };
  }

  if (command === 'RESET') {
    if (args.length > 0) {
      return { ok: false, message: 'Use /RESET.' };
    }

    return { ok: true, action: { type: 'reset' } };
  }

  if (command === 'HELP') {
    if (args.length > 0) {
      return { ok: false, message: 'Use /HELP.' };
    }

    return { ok: true, action: { type: 'help' } };
  }

  if (command === 'QUOTE') {
    const symbol = normalizeSymbol(args[0]);

    if (!symbol || args.length !== 1) {
      return { ok: false, message: 'Use /QUOTE AAPL.' };
    }

    if (!hasSymbol(symbol, options.availableSymbols)) {
      return { ok: false, message: `No quote found: ${symbol}` };
    }

    return { ok: true, action: { type: 'quote', symbol } };
  }

  if (command === 'CHART' || command === 'GRAPH') {
    const symbol = normalizeSymbol(args[0]);

    if (!symbol || args.length !== 1) {
      return { ok: false, message: 'Use /CHART AAPL.' };
    }

    if (!hasSymbol(symbol, options.availableSymbols)) {
      return { ok: false, message: `No quote found: ${symbol}` };
    }

    return { ok: true, action: { type: 'chart', symbol } };
  }

  return { ok: false, message: `Unknown command: /${command}` };
}

export function shouldOpenCommandBar(event: KeyboardEvent): boolean {
  if (event.key !== '/') return false;
  if (event.repeat) return false;
  if (event.altKey || event.ctrlKey || event.metaKey) return false;
  if (isEditableTarget(event.target)) return false;

  return true;
}

export function isEditableTarget(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false;

  const tagName = target.tagName.toLowerCase();

  return (
    tagName === 'input' ||
    tagName === 'textarea' ||
    tagName === 'select' ||
    target.isContentEditable
  );
}

function hasSymbol(symbol: string, availableSymbols?: readonly string[]): boolean {
  if (!availableSymbols || availableSymbols.length === 0) {
    return false;
  }

  return availableSymbols.some((item) => normalizeSymbol(item) === symbol);
}

function normalizeSymbol(value: string | undefined): string {
  return value?.trim().toUpperCase() ?? '';
}